/**
 * O que aconteceu ENTRE duas atualizações do Ranking, e que cena isso merece.
 *
 * Tudo aqui é função pura sobre dois retratos da tela — o do ciclo anterior e o deste. Quem desenha
 * é `cenasDoRanking.js`; quem põe no ar é `CenasDoRanking.jsx`. Separado assim, a regra de quando
 * há festa pode ser testada sem canvas, e é nela que mora o que não pode dar errado: comemorar uma
 * ultrapassagem que não houve é pior do que perder uma que houve.
 */

/** Teto de cenas por ciclo. Uma TV presa em fila de animação deixa de ser um ranking. */
export const MAXIMO_DE_ULTRAPASSAGENS_POR_CICLO = 3;
export const MAXIMO_DE_MARCOS_POR_CICLO = 2;
export const MAXIMO_DE_RAIAS = 6;

/** A meta batida é sempre um marco, mesmo quando a régua não tem faixa exatamente em 100%. */
const MARCO_DA_META = 100;

/**
 * Cor do anel de cada pessoa nas cenas.
 *
 * Sai do id, e não da posição: a mesma pessoa tem a mesma cor em toda cena, em qualquer colocação.
 */
const CORES = ['#38BDF8', '#F472B6', '#34D399', '#FBBF24', '#A78BFA', '#FB923C', '#22D3EE', '#F87171'];

export const corDoVendedor = (id) => {
  const texto = String(id ?? '');
  let soma = 0;
  for (let i = 0; i < texto.length; i += 1) soma = (soma * 31 + texto.charCodeAt(i)) % 9973;

  return CORES[soma % CORES.length];
};

/**
 * O nome como se escreve um nome.
 *
 * O cadastro do IXC vem em maiúsculas. Em caixa alta, "ANDRESA PASSOU GABRIEL!" dentro de uma faixa
 * que já grita vira ruído, e na TV a palavra perde a silhueta que deixa reconhecê-la de longe.
 * Partículas ficam minúsculas.
 */
const PARTICULAS_DE_NOME = new Set(['de', 'da', 'do', 'das', 'dos', 'e']);

export const nomeParaCena = (nome = '') => {
  const limpo = String(nome).trim();

  // Só o que veio TODO em maiúsculas é convertido. Quem cadastrou "IA vendas" escolheu a caixa, e
  // passar por cima disso transformaria a sigla em "Ia".
  return limpo !== limpo.toLocaleUpperCase('pt-BR') ? limpo.replace(/\s+/g, ' ') : emCaixaDeNome(limpo);
};

const emCaixaDeNome = (nome) => nome
  .toLocaleLowerCase('pt-BR')
  .split(/\s+/)
  .filter(Boolean)
  .map((parte, indice) => (indice > 0 && PARTICULAS_DE_NOME.has(parte)
    ? parte
    : parte.charAt(0).toLocaleUpperCase('pt-BR') + parte.slice(1)))
  .join(' ');

/**
 * Retrato de uma corrida: quem está nela, em ordem, e com quantas vendas.
 *
 * Só entra quem já vendeu. Quem está zerado não ocupa posição nenhuma — sem isso, a primeira venda
 * do dia "ultrapassaria" a equipe inteira parada no zero, e a cena da coroa comemoraria a tomada de
 * um trono que ninguém ocupava.
 */
export const retratoDaCorrida = (chave, ranking = []) => ({
  chave,
  corredores: ranking
    .filter((vendedor) => Number(vendedor.contratos) > 0)
    .map((vendedor) => ({ id: String(vendedor.id), contratos: Number(vendedor.contratos) })),
});

/**
 * Quem passou quem entre dois retratos.
 *
 * A comparação é por CONTAGEM, e não por posição na lista. A passa B quando:
 *
 * - **A vendeu.** Posição que muda porque a venda de outro foi cancelada ou corrigida troca as
 *   linhas sem festa;
 * - **antes, B tinha tanto quanto A ou mais** — estava na frente, ou empatado — e já estava na
 *   corrida (quem está zerado não ocupa posição);
 * - **agora A tem mais, e não as mesmas.** Empatar não é passar.
 *
 * A primeira versão comparava a POSIÇÃO de cada um nas duas listas, e perdia a ultrapassagem mais
 * comum de todas. Com os números pequenos do dia, quase ninguém passa direto: A tem 1, B tem 2; A
 * vende e EMPATA; A vende de novo e passa. No ciclo do empate a lista já pode trazer A na frente —
 * o desempate é de quem ordena, e muda de um ciclo para o outro —, e no ciclo seguinte A "já
 * estava" à frente: nenhuma cena, nunca. Pela contagem, o empate não conta e o desempate conta, em
 * qualquer ordem que a lista venha.
 *
 * Trocar a chave (outro período, outra empresa, virada do dia) nunca gera ultrapassagem: ali nada
 * foi conquistado, só mudou o recorte.
 *
 * @returns {Array<{sobe:string, cai:string, posicao:number, passados:number, antes:number}>}
 *   da posição mais alta para a mais baixa; `antes` é quanto quem sobe tinha no ciclo anterior.
 */
export const detectarUltrapassagens = (anterior, atual) => {
  if (!anterior || !atual || anterior.chave !== atual.chave) return [];

  const vendasAntes = new Map(anterior.corredores.map((corredor) => [corredor.id, corredor.contratos]));
  const ultrapassagens = [];

  atual.corredores.forEach((corredor, indice) => {
    const tinha = vendasAntes.get(corredor.id) || 0;
    if (corredor.contratos <= tinha) return;

    const passados = atual.corredores.filter((outro) => outro.id !== corredor.id
      && vendasAntes.has(outro.id)
      && vendasAntes.get(outro.id) >= tinha
      && outro.contratos < corredor.contratos);

    if (passados.length === 0) return;

    ultrapassagens.push({
      sobe: corredor.id,
      // A lista vem da maior contagem para a menor: o primeiro dos passados é o que ficou logo atrás.
      cai: passados[0].id,
      posicao: indice + 1,
      passados: passados.length,
      antes: tinha,
    });
  });

  return ultrapassagens;
};

/**
 * Quem cruzou a meta ou a supermeta do dia entre dois retratos do dia.
 *
 * Mesma regra das vendas novas: abrir a tela ou virar o dia só cria a base, e quem não aparece no
 * retrato anterior não tinha venda. Quem pula a meta e a supermeta no mesmo ciclo comemora só a
 * supermeta.
 *
 * @returns {Array<{id:string, tipo:'meta'|'supermeta'}>}
 */
export const detectarMetasDoDia = (anterior, atual, meta, supermeta) => {
  if (!anterior || !atual || anterior.chave !== atual.chave || !(meta > 0)) return [];

  const vendasAntes = new Map(anterior.corredores.map((corredor) => [corredor.id, corredor.contratos]));
  const cruzou = (antes, agora, alvo) => alvo > 0 && antes < alvo && agora >= alvo;

  return atual.corredores.flatMap((corredor) => {
    const antes = vendasAntes.get(corredor.id) || 0;
    if (cruzou(antes, corredor.contratos, supermeta)) return [{ id: corredor.id, tipo: 'supermeta' }];
    if (cruzou(antes, corredor.contratos, meta)) return [{ id: corredor.id, tipo: 'meta' }];
    return [];
  });
};

/**
 * Quem vendeu entre dois retratos, e quanto.
 *
 * É o que faz o kart andar com som: a venda que não passa ninguém nem cruza faixa nenhuma também
 * merece ser ouvida na sala. Abrir a tela não conta — sem retrato anterior não há venda nova.
 *
 * @returns {Array<{id:string, quantas:number}>}
 */
export const detectarVendasNovas = (anterior, atual) => {
  if (!anterior || !atual || anterior.chave !== atual.chave) return [];

  const vendasAntes = new Map(anterior.corredores.map((corredor) => [corredor.id, corredor.contratos]));

  return atual.corredores
    .map((corredor) => ({ id: corredor.id, quantas: corredor.contratos - (vendasAntes.get(corredor.id) || 0) }))
    .filter((venda) => venda.quantas > 0);
};

/**
 * Retrato das metas individuais: o percentual de cada um e as faixas que valem para ele.
 *
 * `marcosPorTipo` vem da API (`marcos_de_meta`): os percentuais de meta das faixas da régua, por
 * segmento. Sem eles — período legado, intervalo de mais de um mês — o único marco é a meta.
 */
export const retratoDasMetas = (chave, ranking = [], marcosPorTipo = null) => ({
  chave,
  vendedores: ranking
    .filter((vendedor) => vendedor.tem_meta && Number(vendedor.meta_vendas) > 0)
    .map((vendedor) => ({
      id: String(vendedor.id),
      // Multiplica ANTES de dividir. `23 / 20 * 100` dá 114.99999999999999, e quem chegava exatamente
      // numa faixa de 115% ficava de fora até a venda seguinte; `23 * 100 / 20` dá 115.
      percentual: (Number(vendedor.contratos) * 100) / Number(vendedor.meta_vendas),
      marcos: marcosDoVendedor(vendedor, marcosPorTipo),
    })),
});

const marcosDoVendedor = (vendedor, marcosPorTipo) => {
  const daRegua = marcosPorTipo?.[vendedor.tipo_participante] || [];

  return [...new Set([...daRegua.map(Number), MARCO_DA_META])]
    .filter((marco) => Number.isFinite(marco) && marco > 0)
    .sort((a, b) => a - b);
};

/**
 * Que faixa cada um cruzou entre dois retratos.
 *
 * Só a virada conta, como na celebração da meta: abrir o painel com a faixa já cruzada não comemora
 * nada. Cruzar duas no mesmo ciclo comemora só a mais alta — ela contém a outra.
 *
 * @returns {Array<{id:string, pct:number, marcos:number[], bateuAMeta:boolean}>} da faixa mais
 *   alta para a mais baixa.
 */
export const detectarMarcos = (anterior, atual) => {
  if (!anterior || !atual || anterior.chave !== atual.chave) return [];

  const antes = new Map(anterior.vendedores.map((vendedor) => [vendedor.id, vendedor.percentual]));

  return atual.vendedores
    .map((vendedor) => {
      if (!antes.has(vendedor.id)) return null;

      const cruzados = vendedor.marcos.filter((marco) => antes.get(vendedor.id) < marco && vendedor.percentual >= marco);

      if (cruzados.length === 0) return null;

      return {
        id: vendedor.id,
        pct: cruzados[cruzados.length - 1],
        marcos: vendedor.marcos,
        // Bater a META é um marco à parte dos outros: fora do modo apresentação é o único que vira
        // cena, e quem pula de 95% para 120% num ciclo só também a bateu.
        bateuAMeta: antes.get(vendedor.id) < MARCO_DA_META && vendedor.percentual >= MARCO_DA_META,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.pct - a.pct);
};

/**
 * A fila de cenas de um ciclo.
 *
 * - Ultrapassagem: a corrida de hoje tem a preferência, porque é nela que passar alguém é o
 *   assunto; só sem nenhuma no dia entram as do período. A mesma venda costuma causar as duas, e
 *   duas cenas para uma venda só diriam a mesma coisa duas vezes.
 * - Quem assume o 1º lugar do dia ganha a cena da coroa.
 * - A equipe abre a fila (decisão de 03/11/2026): a meta e a supermeta da equipe são de todos e vêm
 *   antes das ultrapassagens e dos marcos individuais. A supermeta contém a meta, então cruzar as
 *   duas comemora só a supermeta.
 *
 * O que passa do teto não vira cena; as linhas trocam de lugar do mesmo jeito.
 */
export const montarFilaDeCenas = ({ doDia = [], doPeriodo = [], marcos = [], equipe = null } = {}) => {
  const recorte = doDia.length ? 'hoje' : 'periodo';
  const ultrapassagens = (doDia.length ? doDia : doPeriodo)
    .slice()
    .sort((a, b) => a.posicao - b.posicao)
    .slice(0, MAXIMO_DE_ULTRAPASSAGENS_POR_CICLO)
    .map((ultrapassagem) => ({
      tipo: 'ultrapassagem',
      recorte,
      ...ultrapassagem,
      coroa: recorte === 'hoje' && ultrapassagem.posicao === 1,
    }));

  return [
    ...(equipe ? [{ tipo: 'equipe', supermeta: equipe === 'supermeta' }] : []),
    ...ultrapassagens,
    ...marcos.slice(0, MAXIMO_DE_MARCOS_POR_CICLO).map((marco) => ({ tipo: 'marco', ...marco })),
  ];
};

/**
 * Junta as cenas de um ciclo novo à fila que ainda não terminou.
 *
 * A cena no ar (a primeira) segue até o fim — cortá-la no meio é pior que esperar. Atrás dela, a
 * equipe passa à frente de tudo o que espera: uma meta da equipe batida agora não fica atrás de três
 * ultrapassagens do ciclo anterior. Entre cenas da mesma prioridade, vale a ordem de chegada.
 */
export const enfileirarCenas = (atuais = [], novas = []) => {
  const [noAr, ...esperando] = atuais;
  const resto = [...esperando, ...novas];
  const daEquipe = resto.filter((cena) => cena.tipo === 'equipe');
  const demais = resto.filter((cena) => cena.tipo !== 'equipe');

  return [...(noAr ? [noAr] : []), ...daEquipe, ...demais];
};

/**
 * O áudio que abre cada cena, no lugar do apito. Um por tipo de evento, para a sala reconhecer de
 * ouvido o que aconteceu antes de olhar. Os arquivos ficam em `public/ranking-cenas/chamadas/`.
 */
const CHAMADAS = {
  venda: 'nova-venda',
  ultrapassagem: 'ultrapassagem-buzina',
  lideranca: 'lideranca-fanfarra-grande',
  faixa: 'faixa-vitoria-curta',
  meta: 'meta-tambores',
  acima: 'acima-moedas',
  equipe: 'equipe-trompetes-triunfais',
  supermeta: 'supermeta-fanfarra-medieval',
};

/** O som de uma venda que não virou cena: toca sozinho, quando o kart anda. */
export const CHAMADA_DE_VENDA = CHAMADAS.venda;

/**
 * Os sons de quem bate a meta ou a supermeta do dia na corrida. Não há arquivo próprio: reaproveitam
 * a vitória curta e as moedas, que são curtos e não abrem cena nenhuma.
 */
export const CHAMADA_DA_META_DO_DIA = CHAMADAS.faixa;
export const CHAMADA_DA_SUPERMETA_DO_DIA = CHAMADAS.acima;

export const chamadaDaCena = (cena) => {
  if (cena.tipo === 'ultrapassagem') return cena.coroa ? CHAMADAS.lideranca : CHAMADAS.ultrapassagem;
  if (cena.tipo === 'equipe') return cena.supermeta ? CHAMADAS.supermeta : CHAMADAS.equipe;
  if (cena.pct < MARCO_DA_META) return CHAMADAS.faixa;

  return cena.pct === MARCO_DA_META ? CHAMADAS.meta : CHAMADAS.acima;
};

/** Só o sufixo de cadastro interno não faz parte da identidade da pessoa. */
export const chaveDoVendedor = (nome) => String(nome || '')
  .trim()
  .toLocaleLowerCase('pt-BR')
  .replace(/\s+\(?(?:vend(?:edor)?[-_/\s]*(?:int|interno)|interno)\)?$/u, '')
  .replace(/\s+/g, ' ')
  .trim();

/**
 * As raias da corrida de hoje.
 *
 * São dos vendedores INTERNOS quando a API informa o segmento (`tipo_participante`, sob a régua):
 * todos eles ganham raia, inclusive quem ainda não vendeu hoje. Sem essa informação — período
 * legado — a pista mostra quem vendeu hoje, e o subtítulo diz isso.
 *
 * Até seis raias: é o que cabe na altura de uma TV sem encolher os karts.
 *
 * Devolve a mesma gente em duas ordens, porque são duas perguntas:
 *
 * - `raias`, em ordem ALFABÉTICA, é como a pista é desenhada. A raia de cada um não muda de lugar
 *   quando alguém passa: a ultrapassagem acontece na pista, que é onde o olho acompanha. Trocar a
 *   linha de lugar e mover o kart ao mesmo tempo seriam dois movimentos para um fato só;
 * - `classificacao`, da 1ª posição para a última, é quem está na frente de quem.
 *
 * @returns {{ somenteInternos: boolean, raias: Array<object>, classificacao: Array<object> }}
 *   cada item com `hoje` (vendas do dia) e `posicao`.
 */
export const corredoresDeHoje = (rankingDoPeriodo = [], rankingDoDia = [], metaDiaria = null) => {
  const vendasHoje = new Map(rankingDoDia.map((vendedor) => [String(vendedor.id), Number(vendedor.contratos) || 0]));
  const chaveNome = (vendedor) => chaveDoVendedor(vendedor?.vendedor_nome || vendedor?.nome || vendedor?.nome_exibicao);
  const vendedoresConhecidos = [...rankingDoDia, ...rankingDoPeriodo];
  const porId = new Map(vendedoresConhecidos.map((vendedor) => [String(vendedor.id), vendedor]));
  const porNome = new Map(vendedoresConhecidos.map((vendedor) => [chaveNome(vendedor), vendedor]));
  const vendedorDoDiaPorId = new Map(rankingDoDia.map((vendedor) => [String(vendedor.id), vendedor]));
  const vendasHojePorNome = new Map(rankingDoDia.filter((vendedor) => chaveNome(vendedor))
    .map((vendedor) => [chaveNome(vendedor), Number(vendedor.contratos) || 0]));
  const metas = Array.isArray(metaDiaria?.vendedores) ? metaDiaria.vendedores : [];
  const internos = rankingDoPeriodo.filter((vendedor) => vendedor.tipo_participante === 'interno');
  const somenteInternos = metas.length > 0 || internos.length > 0;
  const base = metas.length > 0
    ? metas.map((meta) => {
      const nome = chaveNome(meta);
      const porIdEncontrado = porId.get(String(meta.vendedor_id));
      const conhecido = porNome.get(nome) || (porIdEncontrado
        && (!nome || !chaveNome(porIdEncontrado) || chaveNome(porIdEncontrado) === nome)
        ? porIdEncontrado : null);
      return { ...meta, ...(conhecido || {}), meta_necessaria_hoje: meta.meta_necessaria_hoje };
    })
    : (somenteInternos ? internos : rankingDoDia.filter((vendedor) => Number(vendedor.contratos) > 0));

  const vendasDoVendedor = (vendedor) => {
    const nome = chaveNome(vendedor);
    if (nome && vendasHojePorNome.has(nome)) return vendasHojePorNome.get(nome);
    const id = String(vendedor.id || vendedor.vendedor_id);
    const doDia = vendedorDoDiaPorId.get(id);
    if (nome && doDia && chaveNome(doDia) === nome) return vendasHoje.get(id);
    return Number(vendedor.vendas_hoje) || 0;
  };

  const classificacao = base
    .map((vendedor, ordem) => ({
      ...vendedor,
      id: vendedor.id || vendedor.vendedor_id,
      nome: vendedor.nome || vendedor.vendedor_nome,
      hoje: vendasDoVendedor(vendedor),
      ordem,
    }))
    // Empate fica na ordem em que veio: `sort` é estável, e a pista não inventa desempate.
    .sort((a, b) => b.hoje - a.hoje || a.ordem - b.ordem)
    .slice(0, MAXIMO_DE_RAIAS)
    .map(({ ordem: _ordem, ...vendedor }, indice) => ({ ...vendedor, posicao: indice + 1 }));
  const nomeDe = (vendedor) => String(vendedor.nome_exibicao || vendedor.nome || '');
  const raias = classificacao.slice().sort((a, b) => nomeDe(a).localeCompare(nomeDe(b), 'pt-BR'));

  const maiorPlanejada = Math.max(...metas.map((vendedor) => Number(vendedor.meta_planejada_hoje) || 0), 0);
  // A chegada é a meta do dia da EQUIPE interna dividida pelas internas, e a supermeta do dia é a
  // bandeira depois dela. A resposta antiga não traz `meta_do_dia`: aí vale a maior necessária, que
  // no fim do mês subia com quem estava atrasada.
  const metaChegada = Number(metaDiaria?.meta_do_dia) || Number(metaDiaria?.maior_meta_necessaria) || maiorPlanejada;
  const supermetaChegada = Number(metaDiaria?.meta_do_dia) && Number(metaDiaria?.supermeta_do_dia) > metaChegada
    ? Number(metaDiaria.supermeta_do_dia)
    : 0;

  return { somenteInternos, raias, classificacao, metaChegada, supermetaChegada };
};
